<template>
    <section class="px-3 lg:w-full sm:py-12 py-32">
        <div :class="border ? 'bg-gray-950 border border-gray-700 rounded-lg p-8 lg:p-12' : ''">
            <div class="relative flex flex-col lg:flex-row items-start justify-center gap-2 lg:gap-8 max-w-7xl mx-auto"
                :aria-label="`Your product → Unified API → Your customer's data on ${centerLabel}`">

                <!-- LEFT: Your product card -->
                <div class="flex flex-col items-center gap-6 flex-shrink-0">
                    <div
                        class="w-52 h-52 bg-slate-700 rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
                        <!-- Browser header -->
                        <div
                            class="bg-gray-500 border-b border-gray-100 px-3 py-2 flex items-center gap-1.5 flex-shrink-0">
                            <div class="w-2 h-2 rounded-full bg-gray-300"></div>
                            <div class="w-2 h-2 rounded-full bg-gray-300"></div>
                            <div class="w-2 h-2 rounded-full bg-gray-300"></div>
                        </div>

                        <!-- Content area -->
                        <div class="p-4 flex items-center justify-center gap-4 flex-1">
                            <!-- Chart bars -->
                            <div class="flex items-end gap-2 h-12">
                                <div class="w-3 h-10 bg-gray-500 rounded-sm"></div>
                                <div class="w-3 h-7 bg-gray-400 rounded-sm"></div>
                                <div class="w-3 h-11 bg-gray-300 rounded-sm"></div>
                                <div class="w-3 h-8 bg-gray-400 rounded-sm"></div>
                                <div class="w-3 h-10 bg-gray-500 rounded-sm"></div>
                            </div>

                            <!-- Pie chart -->
                            <div class="relative w-10 h-10">
                                <div class="w-full h-full rounded-full bg-gray-500"></div>
                                <div class="absolute top-0 left-0 w-full h-full rounded-full bg-gray-200"
                                    style="clip-path: polygon(50% 50%, 50% 0%, 100% 0%, 100% 50%)"></div>
                            </div>
                        </div>

                        <!-- Bottom rows -->
                        <div class="px-4 pb-4 space-y-2 flex-shrink-0">
                            <div class="w-full h-3 bg-gray-500 rounded-md"></div>
                            <div class="w-full h-3 bg-gray-500 rounded-md"></div>
                            <div class="w-full h-3 bg-gray-500 rounded-md"></div>
                        </div>
                    </div>

                    <div class="text-gray-100 text-xl font-medium flex flex-col items-center">
                        <div>Your product</div>
                        <div class="text-gray-100 text-xs font-medium"> or/and </div>
                        <div>AI agent</div>
                    </div>
                </div>

                <!-- Connector -->
                <div class="hidden lg:block flex-shrink-0 mt-20">
                    <div class="overflow-hidden max-w-24 h-7">
                        <div class="animate-[scroll-right_3s_linear_infinite]">
                            <div class="text-gray-100 text-lg font-medium whitespace-nowrap inline-block">
                                &gt; &gt; &gt; &gt; <span class="text-orange-500">&gt;</span> &gt; &gt; &gt; &gt;
                                &gt; &gt; &gt; &gt; <span class="text-orange-500">&gt;</span> &gt; &gt; &gt; &gt;
                            </div>
                        </div>
                    </div>
                    <div class="overflow-hidden max-w-24 h-7">
                        <div class="animate-[scroll-left_3s_linear_infinite]">
                            <div class="text-gray-100 text-lg font-medium whitespace-nowrap inline-block">
                                &lt; &lt; &lt; &lt; <span class="text-orange-500">&lt;</span> &lt; &lt; &lt; &lt;
                                &lt; &lt; &lt; &lt; <span class="text-orange-500">&lt;</span> &lt; &lt; &lt; &lt;
                            </div>
                        </div>
                    </div>
                    <!-- <div class="flex items-center gap-4">
                        <div class="w-2 h-2 rounded-full bg-orange-500"></div>
                        <div class="w-16 h-0.5 bg-gray-700"></div>
                        <div class="w-2 h-2 rounded-full bg-orange-500"></div>
                    </div> -->
                </div>
                <!-- Connector dots for mobile -->
                <div class="lg:hidden flex flex-col items-center gap-4">
                    <div class="flex flex-col items-center gap-3">
                        <div class="w-2 h-2 rounded-full bg-orange-500"></div>
                        <div class="w-0.5 h-6 bg-gray-700"></div>
                        <div class="w-2 h-2 rounded-full bg-orange-500"></div>
                    </div>
                </div>

                <!-- CENTER: Pill with logo -->
                <div class="flex flex-col items-center gap-6 flex-shrink-0">
                    <div
                        class="w-52 h-52 bg-slate-900 border border-gray-700 flex flex-col items-center justify-center rounded-lg shadow-sm">
                        <img src="https://app.unified.to/unified_logo.png" alt="Unified.to"
                            class="w-28 h-28 object-contain rounded-full shadow-xl" />
                    </div>
                    <div class="text-gray-100 text-xl font-medium flex flex-col items-center">
                        <div>Unified API</div>
                        <div class="text-gray-100 text-xs font-medium"> or/and </div>
                        <div>Unified MCP</div>
                    </div>
                </div>

                <!-- Connector dots for mobile -->
                <div class="lg:hidden flex flex-col items-center gap-4">
                    <div class="flex flex-col items-center gap-3">
                        <div class="w-2 h-2 rounded-full bg-orange-500"></div>
                        <div class="w-0.5 h-6 bg-gray-700"></div>
                        <div class="w-2 h-2 rounded-full bg-orange-500"></div>
                    </div>
                </div>

                <div class="hidden lg:block flex-shrink-0 mt-20">
                    <div class="overflow-hidden max-w-24 h-7">
                        <div class="animate-[scroll-right_3s_linear_infinite]">
                            <div class="text-gray-100 text-lg font-medium whitespace-nowrap inline-block">
                                &gt; &gt; &gt; &gt; <span class="text-orange-500">&gt;</span> &gt; &gt; &gt; &gt;
                                &gt; &gt; &gt; &gt; <span class="text-orange-500">&gt;</span> &gt; &gt; &gt; &gt;
                            </div>
                        </div>
                    </div>
                    <div class="overflow-hidden max-w-24 h-7">
                        <div class="animate-[scroll-left_3s_linear_infinite]">
                            <div class="text-gray-100 text-lg font-medium whitespace-nowrap inline-block">
                                &lt; &lt; &lt; &lt; <span class="text-orange-500">&lt;</span> &lt; &lt; &lt; &lt;
                                &lt; &lt; &lt; &lt; <span class="text-orange-500">&lt;</span> &lt; &lt; &lt; &lt;
                            </div>
                        </div>
                    </div>
                    <!-- <div class="flex items-center gap-4">
                        <div class="w-2 h-2 rounded-full bg-orange-500"></div>
                        <div class="w-16 h-0.5 bg-gray-700"></div>
                        <div class="w-2 h-2 rounded-full bg-orange-500"></div>
                    </div> -->
                </div>

                <!-- RIGHT: Animated list with dashed connectors -->
                <div class="relative flex items-center gap-8 flex-shrink-0 ">
                    <!-- Dashed connectors (desktop only) -->
                    <!-- <div class="hidden lg:block relative w-24 h-64">
                        <svg class="absolute inset-0 w-full h-full" viewBox="0 0 100 260" preserveAspectRatio="none">
                            <path d="M 0 130 Q 50 90, 100 50" class="dash-line" />
                            <path d="M 0 130 Q 50 105, 100 85" class="dash-line" />
                            <path d="M 0 130 Q 50 120, 100 120" class="dash-line" />
                            <path d="M 0 130 Q 50 135, 100 155" class="dash-line" />
                            <path d="M 0 130 Q 50 150, 100 190" class="dash-line" />
                            <path d="M 0 130 Q 50 165, 100 225" class="dash-line" />
                        </svg>
                    </div> -->

                    <!-- Scrolling list container -->
                    <div class="flex flex-col items-center gap-6">
                        <div
                            class="relative w-52 h-52 overflow-hidden rounded-lg border border-gray-200 shadow-sm p-3 bg-gray-800">
                            <!-- Fade mask -->
                            <div
                                class="absolute inset-0 pointer-events-none z-10 bg-gradient-to-b from-gray-950 via-transparent to-gray-950 flex items-center justify-center">
                                <img :src="logoSrc" :alt="centerLabel"
                                    class="w-28 h-28 object-contain rounded-full bg-slate-800/70 p-2" />
                            </div>

                            <!-- Scrolling content -->
                            <div class="h-full overflow-hidden">
                                <div class="flex flex-col animate-[scroll-up_linear_infinite]"
                                    :style="{ animationDuration: `${scrollSeconds}s` }">
                                    <!-- First set -->
                                    <div v-for="(item, i) in normalizedItems" :key="`item1-${i}`"
                                        class="block text-gray-100 text-sm font-medium py-2 select-none transition-opacity"
                                        :class="{ 'opacity-70': item.faded }">
                                        {{ item.label }}
                                    </div>

                                    <!-- Second set (for seamless loop) -->
                                    <div v-for="(item, i) in normalizedItems" :key="`item2-${i}`"
                                        class="block text-gray-100 text-sm font-medium py-2 select-none transition-opacity"
                                        :class="{ 'opacity-70': item.faded }">
                                        {{ item.label }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="text-gray-100 text-xl font-medium text-center">
                            <div>Your customer's data</div>
                            <div>on {{ centerLabel }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script lang="ts">
import { type PropType } from 'vue';

type RightItem = {
    label: string
    href?: string
    faded?: boolean
}

export default {
    props: {
        logoSrc: {
            type: String,
            required: true,
        },
        centerLabel: {
            type: String,
            required: true,
        },
        items: {
            type: Array as PropType<RightItem[]>,
            required: true,
        },
        scrollSeconds: {
            type: Number,
            required: true,
        },
        border: Boolean
    },
    computed: {
        normalizedItems() {
            return this.items.map((it, i) => ({
                label: it?.label ?? `Item ${i + 1}`,
                href: it?.href ?? '#',
                faded: Boolean(it?.faded),
            }))
        },
    },
}
</script>

<style>
.dash-line {
    fill: none;
    stroke: rgba(156, 163, 175, 0.3);
    stroke-width: 2;
    stroke-dasharray: 6 10;
    stroke-linecap: round;
}

@keyframes scroll-up {
    0% {
        transform: translateY(0);
    }

    100% {
        transform: translateY(-50%);
    }
}

@keyframes scroll-left {
    0% {
        transform: translateX(0);
    }

    100% {
        transform: translateX(-50%);
    }
}

@keyframes scroll-right {
    0% {
        transform: translateX(-50%);
    }

    100% {
        transform: translateX(0);
    }
}
</style>